import { useCallback, useEffect } from 'react';
import ContentAssistantLoadingScreen from '../screens/ContentAssistantLoadingScreen';
import useInlineAutocompleteMenuState from './useInlineAutocompleteMenuState';
import PreviewGeneratedContent from '../screens/PreviewGeneratedContent';
import ScreenTextArea from '../screens/ScreenTextArea';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import { GreyContainer, LargeWrapper, PopoverHeader } from '../screens/SharedStyleComponents';
import { useProxiedFetchGeneratedContent, useFetchContentAssistanceContent } from 'content-assistance-lib/hooks/useFetchContentAssistanceContent';
import { useUsageTracker } from '../hooks/useUsageTracker';
import { STARTED, ERROR, REFRESH_ERROR, NOT_STARTED } from '../hooks/constants';
import { MAX_RETRIES } from '../screens/constants';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import ContentAssistantError from '../screens/ContentAssistantError';
import BrandVoiceText from '../core/BrandVoiceText';
import { generateFeedbackAnnotatedResults } from '../api/contentAssistanceApi';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Basic modal menu - used for slash command and selection command with TinyMCE dropdown

const GenerateButton = ({
  onClick,
  isDisabled
}) => {
  const Button = /*#__PURE__*/_jsxs(UIButton, {
    "data-test-id": "content-assistant-generate-button",
    use: "tertiary-light",
    onClick: onClick,
    size: "small",
    disabled: isDisabled,
    children: [/*#__PURE__*/_jsx(UIIcon, {
      name: "refresh",
      size: 14
    }), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "buttons.generateButton"
    })]
  });
  if (isDisabled) {
    return /*#__PURE__*/_jsx(UITooltip, {
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "buttons.generateButtonDisabledTooltip"
      }),
      children: Button
    });
  }
  return Button;
};
const hasValidInput = inputValue => {
  return Boolean((inputValue || '').trim());
};
export const createInlineAutocompleteMenu = useFetchContent => {
  return ({
    brandVoiceOptions,
    currentCommand,
    onPromptComplete,
    onClose,
    editorContext,
    trackInteraction,
    usageDescription,
    language,
    objectId,
    tips
  }) => {
    const trackUsage = useUsageTracker({
      trackInteraction,
      usageDescription
    });
    const {
      state,
      actions
    } = useInlineAutocompleteMenuState();
    const commandId = currentCommand.commandId;
    const parameters = currentCommand.parameters;
    const shouldFetchContentOnModalOpen = currentCommand.shouldFetchContentOnModalOpen;
    const {
      onSuccess,
      setGeneratedContent,
      incrementCount,
      setShowResult,
      setInput
    } = actions;
    const {
      generationInput,
      generatedContent,
      regenerateClickCount,
      selectedResultIndex,
      allResults
    } = state;
    const onError = useCallback(e => {
      console.error('Something went wrong ', e);
    }, []);
    const {
      fetch,
      request,
      sendFeedback,
      error: __error,
      result: __result
    } = useFetchContent({
      onSuccess,
      onError,
      language,
      objectId
    });
    const handleFetch = useCallback(() => {
      const combinedParameters = Object.assign({
        editorContext
      }, parameters);
      if (generationInput) {
        combinedParameters.topic = generationInput;
      }
      fetch({
        commandId,
        parameters: combinedParameters
      });
    }, [commandId, editorContext, fetch, generationInput, parameters]);

    // Edge case - tinyMCE dropdown - with highlighted text - skips the user input modal.
    useEffect(() => {
      if (shouldFetchContentOnModalOpen && request === NOT_STARTED) {
        handleFetch();
      }
      // Only call the first time
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const {
      isSetUp,
      isEnabledForApp
    } = brandVoiceOptions || {};
    const usingBrandVoice = Boolean(isSetUp && isEnabledForApp);
    const onGenerateClicked = useCallback(() => {
      trackUsage('click-generate', {
        text: generationInput,
        command: commandId,
        usingBrandVoice
      });
      handleFetch();
    }, [trackUsage, generationInput, commandId, handleFetch, usingBrandVoice]);
    const onTryAgainClicked = useCallback(() => {
      trackUsage('click-try-again', {
        text: generationInput,
        command: commandId
      });
      handleFetch();
    }, [trackUsage, generationInput, commandId, handleFetch]);
    const onSubmit = useCallback(() => {
      const feedbackResults = generateFeedbackAnnotatedResults(generatedContent, allResults);
      sendFeedback(feedbackResults);
      // Should always(?) be true, but in case it isn't we should have some error fallback
      if (generatedContent && generatedContent.text) {
        onPromptComplete(generatedContent.text);
      }
    }, [allResults, generatedContent, onPromptComplete, sendFeedback]);
    const handleClose = useCallback(() => {
      onClose();
      const feedbackAnnotatedResults = generateFeedbackAnnotatedResults(generatedContent, allResults, false);
      sendFeedback(feedbackAnnotatedResults);
    }, [allResults, generatedContent, onClose, sendFeedback]);
    const handleRefresh = useCallback(() => {
      incrementCount();
      handleFetch();
    }, [handleFetch, incrementCount]);
    const headerText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: currentCommand.headerKey
    });
    const brandVoiceLink = isSetUp && isEnabledForApp && currentCommand && currentCommand.brandVoiceLinkKey && /*#__PURE__*/_jsx(BrandVoiceText, {
      brandVoiceLinkKey: currentCommand.brandVoiceLinkKey,
      brandVoiceOptions: brandVoiceOptions
    });
    const isLoading = request === STARTED ||
    // Show loading immediately if we're gonna fetch
    shouldFetchContentOnModalOpen && request === NOT_STARTED;
    if (isLoading) {
      return /*#__PURE__*/_jsx(ContentAssistantLoadingScreen, {
        label: headerText,
        body: brandVoiceLink,
        onCancel: onClose,
        tips: tips
      });
    } else if (request === ERROR || request === REFRESH_ERROR) {
      return /*#__PURE__*/_jsx(ContentAssistantError, {
        label: headerText,
        body: brandVoiceLink,
        onCancel: onClose,
        onTryAgain: onTryAgainClicked
      });
    } else if (allResults.length > 0) {
      const trackingProperties = {
        command: commandId,
        // regenerateClickCount starts at 0 but should be tracked starting from 1
        regenerateClickCount: regenerateClickCount + 1,
        usingBrandVoice
      };
      return /*#__PURE__*/_jsx(PreviewGeneratedContent, {
        content: generatedContent,
        updateContent: setGeneratedContent,
        onSubmit: onSubmit,
        onCancel: handleClose,
        onRefresh: handleRefresh,
        isRefreshDisabled: regenerateClickCount >= MAX_RETRIES,
        label: headerText,
        body: brandVoiceLink,
        setShowResult: setShowResult,
        resultCount: allResults.length,
        selectedResultIndex: selectedResultIndex,
        trackUsage: trackUsage,
        trackingProperties: trackingProperties
      });
    }
    return /*#__PURE__*/_jsxs(LargeWrapper, {
      "data-test-id": "initial-auto-complete-screen",
      children: [/*#__PURE__*/_jsx(PopoverHeader, {
        onCancel: onClose,
        label: headerText,
        body: brandVoiceLink
      }), /*#__PURE__*/_jsx(ScreenTextArea, {
        content: generationInput,
        updateContent: setInput,
        placeholder: currentCommand.placeholder && I18n.text(currentCommand.placeholder),
        focus: true,
        onKeyDown: e => {
          const isComposingWord = e.nativeEvent.isComposing;
          if (e.key === 'Enter' && !e.shiftKey && !isComposingWord && hasValidInput(generationInput)) {
            onGenerateClicked();
          }
        }
      }), /*#__PURE__*/_jsx(GreyContainer, {
        hideNavigation: true,
        children: /*#__PURE__*/_jsx(GenerateButton, {
          onClick: onGenerateClicked,
          isDisabled: !hasValidInput(generationInput)
        })
      })]
    });
  };
};
export const ProxiedInlineAutocompleteMenu = createInlineAutocompleteMenu(useProxiedFetchGeneratedContent);
export const InlineAutocompleteMenu = createInlineAutocompleteMenu(useFetchContentAssistanceContent);